/**
 * Period Kit Quiz - Single Page Application
 * Manages quiz state and navigation through 8 steps
 */

class PeriodKitQuiz {
  constructor() {
    this.quizSession = this.loadSession();
    this.currentStep = this.quizSession.currentStep || 1;
    this.totalSteps = 8;
    
    // Size chart data: use metaobject from product page when available (window.sizeGuideMeasurements / window.sizeGuideMeasurementsOrder)
    const fallbackData = {
      '2XS': { hip: { in: [23, 27], cm: [60, 69] }, waist: { in: [19, 23], cm: [50, 59] } },
      'XS': { hip: { in: [27, 31], cm: [70, 79] }, waist: { in: [23, 27], cm: [60, 69] } },
      'S': { hip: { in: [31, 35], cm: [80, 89] }, waist: { in: [27, 31], cm: [70, 79] } },
      'M': { hip: { in: [35, 38], cm: [90, 97] }, waist: { in: [31, 34], cm: [80, 88] } },
      'L': { hip: { in: [38, 41], cm: [98, 104] }, waist: { in: [34, 37], cm: [89, 94] } },
      'XL': { hip: { in: [41, 44], cm: [105, 112] }, waist: { in: [37, 40], cm: [95, 102] } },
      '2XL': { hip: { in: [44, 47], cm: [113, 119] }, waist: { in: [40, 43], cm: [103, 109] } },
      '3XL': { hip: { in: [47, 52], cm: [120, 134] }, waist: { in: [43, 46], cm: [110, 118] } },
      '4XL': { hip: { in: [50, 57], cm: [132, 146] }, waist: { in: [46, 51], cm: [119, 128] } }
    };
    this.sizeChartData = (typeof window.sizeGuideMeasurements === 'object' && window.sizeGuideMeasurements) ? window.sizeGuideMeasurements : fallbackData;
    this.sizeOrder = (Array.isArray(window.sizeGuideMeasurementsOrder) && window.sizeGuideMeasurementsOrder.length) ? window.sizeGuideMeasurementsOrder : ['2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

    this.init();
  }

  loadSession() {
    try {
      const sessionData = sessionStorage.getItem('periodKitQuizSession');
      if (sessionData) {
        return JSON.parse(sessionData);
      }
    } catch (e) {
      console.error('Error loading session:', e);
    }
    return {
      name: null,
      cycleDays: null,
      currentProductType: null,
      usageByDay: [],
      size: null,
      style: null,
      color: null,
      contact: { email: null, phone: null },
      currentStep: 1
    };
  }

  saveSession() {
    try {
      this.quizSession.currentStep = this.currentStep;
      sessionStorage.setItem('periodKitQuizSession', JSON.stringify(this.quizSession));
    } catch (e) {
      console.error('Error saving session:', e);
    }
  }

  clearSession() {
    sessionStorage.removeItem('periodKitQuizSession');
    this.quizSession = {
      name: null,
      cycleDays: null,
      currentProductType: null,
      usageByDay: [],
      size: null,
      style: null,
      color: null,
      contact: { email: null, phone: null },
      currentStep: 1
    };
  }

  init() {
    // Check if we're on the quiz page, if not redirect
    if (!document.getElementById('period-kit-quiz')) {
      return;
    }

    // If page is refreshed, clear session and restart
    if (performance.navigation && performance.navigation.type === 1) {
      this.clearSession();
      this.currentStep = 1;
    }

    this.setupEventListeners();
    this.renderStep(this.currentStep);
    this.updateProgress();
  }

  setupEventListeners() {
    // Step 1: Name input
    const nameInput = document.getElementById('quiz-name');
    const nextStep1 = document.getElementById('next-step-1');
    if (nameInput) {
      nameInput.addEventListener('input', (e) => {
        nextStep1.disabled = !e.target.value.trim();
        this.quizSession.name = e.target.value.trim();
        this.saveSession();
      });
      nameInput.value = this.quizSession.name || '';
      nextStep1.disabled = !nameInput.value.trim();
    }
    if (nextStep1) {
      nextStep1.addEventListener('click', () => this.goToStep(2));
    }

    // Step 2: Cycle days
    const cycleDaysSelect = document.getElementById('quiz-cycle-days');
    const nextStep2 = document.getElementById('next-step-2');
    const prevStep2 = document.getElementById('prev-step-2');
    if (cycleDaysSelect) {
      cycleDaysSelect.value = this.quizSession.cycleDays || '';
      cycleDaysSelect.addEventListener('change', (e) => {
        this.quizSession.cycleDays = parseInt(e.target.value);
        nextStep2.disabled = !e.target.value;
        this.saveSession();
      });
      nextStep2.disabled = !cycleDaysSelect.value;
    }
    if (nextStep2) {
      nextStep2.addEventListener('click', () => this.goToStep(3));
    }
    if (prevStep2) {
      prevStep2.addEventListener('click', () => this.goToStep(1));
    }

    // Step 3: Product type
    const productTypeButtons = document.querySelectorAll('[data-value="pads"], [data-value="cups"], [data-value="tampons"]');
    const nextStep3 = document.getElementById('next-step-3');
    const prevStep3 = document.getElementById('prev-step-3');
    productTypeButtons.forEach(btn => {
      if (btn.dataset.value === this.quizSession.currentProductType) {
        btn.classList.add('selected');
        nextStep3.disabled = false;
      }
      btn.addEventListener('click', (e) => {
        productTypeButtons.forEach(b => b.classList.remove('selected'));
        e.currentTarget.classList.add('selected');
        this.quizSession.currentProductType = e.currentTarget.dataset.value;
        nextStep3.disabled = false;
        this.saveSession();
        this.updateStep4Title();
      });
    });
    if (nextStep3) {
      nextStep3.addEventListener('click', () => this.goToStep(4));
    }
    if (prevStep3) {
      prevStep3.addEventListener('click', () => this.goToStep(2));
    }

    // Step 4: Usage by day
    const nextStep4 = document.getElementById('next-step-4');
    const prevStep4 = document.getElementById('prev-step-4');
    if (nextStep4) {
      nextStep4.addEventListener('click', () => this.validateStep4() && this.goToStep(5));
    }
    if (prevStep4) {
      prevStep4.addEventListener('click', () => this.goToStep(3));
    }

    // Step 5: Size (replicates size-guide-drawer__content - calculator, recommendation, size chart)
    const step5 = document.getElementById('step-5');
    const nextStep5 = document.getElementById('next-step-5');
    const prevStep5 = document.getElementById('prev-step-5');
    const sectionId = step5?.getAttribute('data-quiz-section-id');
    const sid = step5?.getAttribute('data-quiz-size-section-id') || ('quiz-size-' + sectionId);

    if (step5 && sectionId) {
      const container = step5.querySelector('.quiz-step__size-guide-content') || step5;
      const jeansInput = container.querySelector('#quiz-jeans-input-' + sectionId);
      const pantyInput = container.querySelector('#quiz-panty-input-' + sectionId);
      const howToMeasure = container.querySelector('#how-to-measure-' + sid);
      const collapsible = container.querySelector('[data-collapsible="' + sid + '"]');
      const collapsibleTrigger = container.querySelector('[data-collapsible-trigger="' + sid + '"]');
      const recommendation = container.querySelector('#quiz-size-recommendation-' + sectionId);
      const recommendationActions = container.querySelector('#quiz-recommendation-actions-' + sectionId);
      const selectSizeBtn = container.querySelector('#quiz-select-size-btn-' + sectionId);
      const sizeChart = container.querySelector('#quiz-size-chart-' + sectionId);
      const sizeChartTable = container.querySelector('[data-quiz-size-chart="' + sid + '"]');

      const getUnitForInput = (input) => {
        if (!input) return 'in';
        const wrapper = input.closest('.size-guide-drawer__input-wrapper');
        if (!wrapper) return 'in';
        const active = wrapper.querySelector('.size-guide-drawer__unit-button.active');
        return active ? active.getAttribute('data-unit') : 'in';
      };

      const updateSizeChartTable = (unit) => {
        if (!sizeChartTable) return;
        const rows = sizeChartTable.querySelectorAll('tbody tr');
        rows.forEach(row => {
          const hipCell = row.querySelector('td[data-hip-in]');
          const waistCell = row.querySelector('td[data-waist-in]');
          if (hipCell && waistCell) {
            hipCell.textContent = unit === 'cm' ? hipCell.getAttribute('data-hip-cm') : hipCell.getAttribute('data-hip-in');
            waistCell.textContent = unit === 'cm' ? waistCell.getAttribute('data-waist-cm') : waistCell.getAttribute('data-waist-in');
          }
        });
      };

      const syncCalculatorToChart = () => {
        const unit = jeansInput ? getUnitForInput(jeansInput) : 'in';
        container.querySelectorAll('[data-chart-unit="' + sid + '"]').forEach(btn => {
          btn.classList.toggle('active', btn.getAttribute('data-unit') === unit);
        });
        updateSizeChartTable(unit);
      };

      const showRecommendation = (recommendedSize, jeansVal, jeansUnit, pantyVal, pantyUnit) => {
        if (howToMeasure) howToMeasure.style.display = 'none';
        if (collapsible) collapsible.style.display = 'none';
        if (sizeChart) {
          sizeChart.style.display = 'none';
          sizeChart.classList.remove('visible');
        }
        if (recommendation) {
          recommendation.style.display = 'flex';
          const sizeEl = recommendation.querySelector('#quiz-recommended-size-' + sectionId);
          if (sizeEl) sizeEl.textContent = recommendedSize;
          const jeansValEl = recommendation.querySelector('#quiz-jeans-value-' + sectionId);
          const pantyValEl = recommendation.querySelector('#quiz-panty-value-' + sectionId);
          if (jeansValEl) jeansValEl.textContent = jeansVal + ' ' + jeansUnit;
          if (pantyValEl) pantyValEl.textContent = pantyVal + ' ' + pantyUnit;
          if (sizeChartTable) {
            sizeChartTable.querySelectorAll('input[type="radio"]').forEach(radio => {
              radio.checked = radio.getAttribute('data-size-value') === recommendedSize;
            });
          }
        }
        if (recommendationActions) recommendationActions.style.display = 'flex';
        if (selectSizeBtn) selectSizeBtn.style.display = 'block';
      };

      const showCalculator = () => {
        if (howToMeasure) howToMeasure.style.display = '';
        if (collapsible) {
          collapsible.style.display = '';
          collapsible.classList.add('is-open');
        }
        if (collapsibleTrigger) collapsibleTrigger.setAttribute('aria-expanded', 'true');
        if (recommendation) recommendation.style.display = 'none';
        if (recommendationActions) recommendationActions.style.display = 'none';
        if (sizeChart) {
          sizeChart.style.display = 'block';
          sizeChart.classList.remove('visible');
        }
        if (selectSizeBtn) selectSizeBtn.style.display = 'block';
      };

      const selectSizeForQuiz = (sizeValue) => {
        this.quizSession.size = sizeValue;
        nextStep5.disabled = false;
        this.saveSession();
      };

      // Collapsible toggle
      if (collapsibleTrigger && collapsible) {
        collapsibleTrigger.addEventListener('click', () => {
          const isOpen = collapsible.classList.toggle('is-open');
          collapsibleTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
      }

      // Calculate button
      const calcBtn = container.querySelector('[data-quiz-calculate="' + sid + '"]');
      if (calcBtn && jeansInput && pantyInput) {
        calcBtn.addEventListener('click', () => {
          const jeansVal = jeansInput.value.trim();
          const pantyVal = pantyInput.value.trim();
          if (!jeansVal || !pantyVal) {
            alert('Please fill the size inputs');
            return;
          }
          const jeansUnit = getUnitForInput(jeansInput);
          const pantyUnit = getUnitForInput(pantyInput);
          const jeansNum = parseFloat(jeansVal);
          const pantyNum = parseFloat(pantyVal);
          if (isNaN(jeansNum) || isNaN(pantyNum)) {
            alert('Please enter valid numbers');
            return;
          }
          const recommendedSize = this.calculateSize(jeansVal, pantyVal, jeansUnit, pantyUnit);
          if (recommendedSize) {
            showRecommendation(recommendedSize, jeansVal, jeansUnit, pantyVal, pantyUnit);
            syncCalculatorToChart();
          }
        });
      }

      // Edit links
      container.querySelectorAll('[data-quiz-edit]').forEach(btn => {
        btn.addEventListener('click', showCalculator);
      });

      // View Size Chart
      const viewChartBtn = container.querySelector('[data-quiz-view-chart="' + sid + '"]');
      if (viewChartBtn && sizeChart) {
        viewChartBtn.addEventListener('click', () => {
          const recSizeEl = container.querySelector('#quiz-recommended-size-' + sectionId);
          const recSize = recSizeEl ? recSizeEl.textContent.trim() : '';
          if (recSize && sizeChartTable) {
            sizeChartTable.querySelectorAll('input[type="radio"]').forEach(radio => {
              radio.checked = radio.getAttribute('data-size-value') === recSize;
            });
          }
          sizeChart.style.display = 'block';
          sizeChart.classList.add('visible');
          if (selectSizeBtn) selectSizeBtn.style.display = 'none';
          sizeChart.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }

      // Select This Size (from recommendation)
      const selectSizeRecBtn = container.querySelector('[data-quiz-select-size="' + sid + '"]');
      if (selectSizeRecBtn) {
        selectSizeRecBtn.addEventListener('click', () => {
          const recSizeEl = container.querySelector('#quiz-recommended-size-' + sectionId);
          if (recSizeEl) {
            selectSizeForQuiz(recSizeEl.textContent.trim());
          }
        });
      }

      // Select This Size (from chart)
      const selectChartBtn = container.querySelector('[data-quiz-select-chart="' + sid + '"]');
      if (selectChartBtn && sizeChartTable) {
        selectChartBtn.addEventListener('click', () => {
          const checked = sizeChartTable.querySelector('input[type="radio"]:checked');
          if (checked) {
            selectSizeForQuiz(checked.getAttribute('data-size-value'));
          } else {
            alert('Please select a size from the chart');
          }
        });
      }

      // Chart unit toggle
      container.querySelectorAll('[data-chart-unit="' + sid + '"]').forEach(btn => {
        btn.addEventListener('click', function() {
          container.querySelectorAll('[data-chart-unit="' + sid + '"]').forEach(b => b.classList.remove('active'));
          this.classList.add('active');
          const unit = this.getAttribute('data-unit');
          updateSizeChartTable(unit);
          container.querySelectorAll('.size-guide-drawer__unit-button[data-input="jeans-' + sid + '"], .size-guide-drawer__unit-button[data-input="panty-' + sid + '"]').forEach(b => {
            b.classList.toggle('active', b.getAttribute('data-unit') === unit);
          });
        });
      });

      // Calculator unit toggle
      container.querySelectorAll('.size-guide-drawer__unit-button[data-input]').forEach(btn => {
        btn.addEventListener('click', function() {
          const wrapper = this.closest('.size-guide-drawer__unit-toggle');
          if (!wrapper) return;
          const input = wrapper.closest('.size-guide-drawer__input-wrapper')?.querySelector('input');
          const curUnit = input ? getUnitForInput(input) : 'in';
          wrapper.querySelectorAll('.size-guide-drawer__unit-button').forEach(b => b.classList.remove('active'));
          this.classList.add('active');
          const newUnit = this.getAttribute('data-unit');
          if (input && input.value) {
            const val = parseFloat(input.value);
            if (!isNaN(val) && curUnit !== newUnit) {
              input.value = newUnit === 'cm' ? (val * 2.54).toFixed(1) : (val / 2.54).toFixed(1);
            }
          }
          syncCalculatorToChart();
        });
      });

      // Next: save raw values for session restore
      if (nextStep5) {
        nextStep5.addEventListener('click', () => {
          if (jeansInput) sessionStorage.setItem('quizWaistSize', jeansInput.value);
          if (pantyInput) sessionStorage.setItem('quizHipSize', pantyInput.value);
          this.goToStep(6);
        });
      }

      // Restore session: if we have a size, enable Next
      if (this.quizSession.size) {
        nextStep5.disabled = false;
        if (sizeChartTable) {
          sizeChartTable.querySelectorAll('input[type="radio"]').forEach(radio => {
            if (radio.getAttribute('data-size-value') === this.quizSession.size) radio.checked = true;
          });
        }
      }
      updateSizeChartTable('in');
    }
    if (prevStep5) {
      prevStep5.addEventListener('click', () => this.goToStep(4));
    }

    // Step 6: Style
    const styleButtons = document.querySelectorAll('[data-value^="hipster-"]');
    const nextStep6 = document.getElementById('next-step-6');
    const prevStep6 = document.getElementById('prev-step-6');
    styleButtons.forEach(btn => {
      if (btn.dataset.value === this.quizSession.style) {
        btn.classList.add('selected');
        nextStep6.disabled = false;
      }
      btn.addEventListener('click', (e) => {
        styleButtons.forEach(b => b.classList.remove('selected'));
        e.currentTarget.classList.add('selected');
        this.quizSession.style = e.currentTarget.dataset.value;
        nextStep6.disabled = false;
        this.saveSession();
      });
    });
    if (nextStep6) {
      nextStep6.addEventListener('click', () => this.goToStep(7));
    }
    if (prevStep6) {
      prevStep6.addEventListener('click', () => this.goToStep(5));
    }

    // Step 7: Color (dynamic - uses theme Color Swatch Mapping)
    const colorContainer = document.getElementById('quiz-color-options');
    const colorButtons = colorContainer ? colorContainer.querySelectorAll('.quiz-step__option-wrap--color[data-value]') : [];
    const nextStep7 = document.getElementById('next-step-7');
    const prevStep7 = document.getElementById('prev-step-7');
    colorButtons.forEach(btn => {
      if (btn.dataset.value === this.quizSession.color) {
        btn.classList.add('selected');
        nextStep7.disabled = false;
      }
      btn.addEventListener('click', (e) => {
        colorButtons.forEach(b => b.classList.remove('selected'));
        e.currentTarget.classList.add('selected');
        this.quizSession.color = e.currentTarget.dataset.value;
        nextStep7.disabled = false;
        this.saveSession();
      });
    });
    if (nextStep7) {
      nextStep7.addEventListener('click', () => this.goToStep(8));
    }
    if (prevStep7) {
      prevStep7.addEventListener('click', () => this.goToStep(6));
    }

    // Step 8: Contact
    const emailInput = document.getElementById('quiz-email');
    const phoneInput = document.getElementById('quiz-phone');
    const nextStep8 = document.getElementById('next-step-8');
    const skipStep8 = document.getElementById('skip-step-8');
    const prevStep8 = document.getElementById('prev-step-8');
    
    if (emailInput) {
      emailInput.value = this.quizSession.contact?.email || '';
      emailInput.addEventListener('input', (e) => {
        this.quizSession.contact = this.quizSession.contact || {};
        this.quizSession.contact.email = e.target.value.trim();
        this.saveSession();
      });
    }
    
    if (phoneInput) {
      phoneInput.value = this.quizSession.contact?.phone || '';
      phoneInput.addEventListener('input', (e) => {
        this.quizSession.contact = this.quizSession.contact || {};
        this.quizSession.contact.phone = e.target.value.trim();
        this.saveSession();
      });
    }

    if (nextStep8) {
      nextStep8.addEventListener('click', async () => {
        if (this.validateEmail(emailInput.value)) {
          this.quizSession.contact = {
            email: emailInput.value.trim(),
            phone: phoneInput.value.trim() || null
          };
          this.saveSession();
          await this.sendWebsizeInfo(this.quizSession, true);
          this.submitQuiz(true);
        } else {
          alert('Please enter a valid email address');
        }
      });
    }

    if (skipStep8) {
      skipStep8.addEventListener('click', () => {
        this.submitQuiz(false);
      });
    }

    if (prevStep8) {
      prevStep8.addEventListener('click', () => this.goToStep(7));
    }
  }

  // Helper function to convert cm to inches
  cmToInches(cm) {
    return cm / 2.54;
  }

  // Helper function to compare sizes and return the larger one
  getLargerSize(size1, size2) {
    if (!size1) return size2;
    if (!size2) return size1;
    
    const index1 = this.sizeOrder.indexOf(size1);
    const index2 = this.sizeOrder.indexOf(size2);
    
    if (index1 === -1) return size2;
    if (index2 === -1) return size1;
    
    return index1 > index2 ? size1 : size2;
  }

  // Find which size a measurement falls into
  findSizeForMeasurement(measurement, measurementType) {
    // measurementType: 'waist' or 'hip'
    for (const [size, ranges] of Object.entries(this.sizeChartData)) {
      const range = ranges[measurementType].in;
      if (measurement >= range[0] && measurement <= range[1]) {
        return size;
      }
    }
    
    // If no exact match, find closest size
    let closestSize = null;
    let minDistance = Infinity;
    
    for (const [size, ranges] of Object.entries(this.sizeChartData)) {
      const range = ranges[measurementType].in;
      const mid = (range[0] + range[1]) / 2;
      const distance = Math.abs(measurement - mid);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestSize = size;
      }
    }
    
    return closestSize;
  }

  // Calculate recommended size based on measurements (matching product page size guide)
  // Supports separate units for waist and hip (e.g. waist in inches, hip in cm)
  calculateSize(waistValue, hipValue, waistUnit = 'in', hipUnit = 'in') {
    const waist = parseFloat(waistValue);
    const hip = parseFloat(hipValue);
    
    if (isNaN(waist) || isNaN(hip)) {
      return null;
    }

    const waistIn = waistUnit === 'cm' ? this.cmToInches(waist) : waist;
    const hipIn = hipUnit === 'cm' ? this.cmToInches(hip) : hip;

    const waistSize = this.findSizeForMeasurement(waistIn, 'waist');
    const hipSize = this.findSizeForMeasurement(hipIn, 'hip');
    
    return this.getLargerSize(waistSize, hipSize);
  }

  displaySizeGuide(size) {
    const sizeGuide = document.getElementById('size-guide');
    if (sizeGuide) {
      sizeGuide.innerHTML = `
        <div class="size-guide">
          <div class="size-guide__title">Your recommended size:</div>
          <div class="size-guide__value">${size}</div>
        </div>
      `;
    }
  }

  updateStep4Title() {
    const title = document.getElementById('step-4-title');
    if (!title) return;
    
    const productType = this.quizSession.currentProductType;
    const titles = window.periodKitQuizStrings?.titleStep4 || {
      pads: 'How many pads do you usually use in 12 hours?',
      tampons: 'How many tampons do you usually use in 12 hours?',
      cups: 'How many cups do you usually use in 12 hours?'
    };
    title.textContent = titles[productType] || titles.pads;
  }

  renderStep4() {
    const wrapper = document.getElementById('usage-by-day-wrapper');
    if (!wrapper || !this.quizSession.cycleDays || !this.quizSession.currentProductType) {
      return;
    }

    const productType = this.quizSession.currentProductType;
    const productNames = window.periodKitQuizStrings?.productNames || { pads: 'pads', tampons: 'tampons', cups: 'cups' };
    const productName = productNames[productType] || 'products';
    const productNameCapitalized = productName.charAt(0).toUpperCase() + productName.slice(1);
    const dayLabelTemplate = window.periodKitQuizStrings?.usageDayLabel || 'Day __day__';
    const selectPlaceholderTemplate = window.periodKitQuizStrings?.usageSelectPlaceholder || 'Select Number of __product__ Used';
    const selectPlaceholder = selectPlaceholderTemplate.replace(/__product__/g, productNameCapitalized);

    wrapper.innerHTML = '';
    
    for (let day = 1; day <= this.quizSession.cycleDays; day++) {
      const existingValue = this.quizSession.usageByDay.find(u => u.day === day)?.units || '';
      const dayLabel = dayLabelTemplate.replace(/__day__/g, day);
      
      const dayItem = document.createElement('div');
      dayItem.className = 'usage-day-item';
      dayItem.innerHTML = `
        <span class="usage-day-item__label">${dayLabel}</span>
        <select class="quiz-step__select quiz-step__select--usage usage-select" data-day="${day}" required>
          <option value="">${selectPlaceholder}</option>
          <option value="1" ${existingValue === 1 ? 'selected' : ''}>1</option>
          <option value="2" ${existingValue === 2 ? 'selected' : ''}>2</option>
          <option value="3" ${existingValue === 3 ? 'selected' : ''}>3</option>
          <option value="4" ${existingValue === 4 ? 'selected' : ''}>4</option>
          <option value="5" ${existingValue === 5 ? 'selected' : ''}>5</option>
        </select>
      `;
      wrapper.appendChild(dayItem);
    }

    // Add event listeners
    const selects = wrapper.querySelectorAll('.usage-select');
    const nextButton = document.getElementById('next-step-4');
    selects.forEach(select => {
      select.addEventListener('change', () => {
        this.validateStep4();
      });
    });

    this.validateStep4();
  }

  validateStep4() {
    const wrapper = document.getElementById('usage-by-day-wrapper');
    const nextButton = document.getElementById('next-step-4');
    if (!wrapper || !nextButton) return false;

    const selects = wrapper.querySelectorAll('.usage-select');
    let allFilled = true;
    const usageByDay = [];

    selects.forEach(select => {
      const day = parseInt(select.dataset.day);
      const units = parseInt(select.value);
      if (!units) {
        allFilled = false;
      } else {
        usageByDay.push({ day, units });
      }
    });

    if (allFilled && usageByDay.length === this.quizSession.cycleDays) {
      this.quizSession.usageByDay = usageByDay;
      nextButton.disabled = false;
      this.saveSession();
      return true;
    } else {
      nextButton.disabled = true;
      return false;
    }
  }

  renderStep(stepNumber) {
    // Hide all steps
    for (let i = 1; i <= this.totalSteps; i++) {
      const step = document.getElementById(`step-${i}`);
      if (step) {
        step.style.display = i === stepNumber ? 'block' : 'none';
      }
    }

    // Special rendering for step 4
    if (stepNumber === 4) {
      this.renderStep4();
      this.updateStep4Title();
    }

    this.currentStep = stepNumber;
    this.updateProgress();
  }

  updateProgress() {
    const progressBar = document.getElementById('progress-bar');
    const progressBarActive = document.getElementById('progress-bar-active');
    const progressStepWrappers = document.querySelectorAll('.period-kit-quiz__progress-step-wrapper');

    // Update step indicators first
    progressStepWrappers.forEach((wrapper, index) => {
      const stepNum = index + 1;
      wrapper.classList.remove('active', 'completed');
      if (stepNum < this.currentStep) {
        wrapper.classList.add('completed');
      } else if (stepNum === this.currentStep) {
        wrapper.classList.add('active');
      }
    });

    // Calculate the width of the progress bars after DOM updates
    // Dark blue line extends through all completed steps
    // Pink line extends from start of current step
    requestAnimationFrame(() => {
      if (progressBar && progressStepWrappers.length > 0) {
        const progressContainer = document.getElementById('quiz-progress');
        const firstStep = progressStepWrappers[0];
        const currentStepWrapper = progressStepWrappers[this.currentStep - 1];
        const previousStepWrapper = this.currentStep > 1 ? progressStepWrappers[this.currentStep - 2] : null;
        
        if (firstStep && currentStepWrapper && progressContainer) {
          const containerRect = progressContainer.getBoundingClientRect();
          const firstStepRect = firstStep.getBoundingClientRect();
          const currentStepRect = currentStepWrapper.getBoundingClientRect();
          
          // Calculate positions relative to container
          const firstStepLeft = firstStepRect.left - containerRect.left;
          const currentStepLeft = currentStepRect.left - containerRect.left;
          const currentStepCenter = currentStepLeft + (currentStepRect.width / 2);
          
          const currentStepRight = currentStepLeft + currentStepRect.width;
          
          // Dark blue progress bar extends to the end of previous completed step
          if (previousStepWrapper) {
            const previousStepRect = previousStepWrapper.getBoundingClientRect();
            const previousStepRight = (previousStepRect.left - containerRect.left) + previousStepRect.width;
            progressBar.style.width = `${previousStepRight - firstStepLeft}px`;
          } else {
            // If on first step, no dark blue bar
            progressBar.style.width = '0px';
          }
          
          // Pink progress bar for active step - extends from end of dark blue to end of current step
          if (progressBarActive) {
            if (previousStepWrapper) {
              const previousStepRect = previousStepWrapper.getBoundingClientRect();
              const previousStepRight = (previousStepRect.left - containerRect.left) + previousStepRect.width;
              progressBarActive.style.left = `${previousStepRight - firstStepLeft}px`;
              progressBarActive.style.width = `${currentStepRight - previousStepRight}px`;
            } else {
              // If on first step, pink bar starts from beginning
              progressBarActive.style.left = `${firstStepLeft}px`;
              progressBarActive.style.width = `${currentStepRight - firstStepLeft}px`;
            }
          }
        }
      }
    });
  }

  goToStep(stepNumber) {
    if (stepNumber < 1 || stepNumber > this.totalSteps) return;
    this.renderStep(stepNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  buildResultPageUrl(shouldSendEmail) {
    // Build query parameters
    const params = new URLSearchParams();
    params.set('name', this.quizSession.name || '');
    params.set('cycleDays', this.quizSession.cycleDays || '');
    params.set('productType', this.quizSession.currentProductType || '');
    params.set('size', this.quizSession.size || '');
    params.set('style', this.quizSession.style || '');
    params.set('color', this.quizSession.color || '');
    params.set('usageByDay', JSON.stringify(this.quizSession.usageByDay));
    
    if (shouldSendEmail && this.quizSession.contact?.email) {
      params.set('email', this.quizSession.contact.email);
      if (this.quizSession.contact.phone) {
        params.set('phone', this.quizSession.contact.phone);
      }
    }

    return '/pages/build-your-period-kit-result?' + params.toString();
  }

  async submitQuiz(shouldSendEmail) {
    if (shouldSendEmail && this.quizSession.contact?.email) {
      // Send email via Google Apps Script proxy (if configured)
      try {
        await this.sendEmail(this.quizSession);
      } catch (error) {
        console.error('Error sending email:', error);
        // Continue even if email fails
      }
    }

    // Redirect to result page
    const resultPageUrl = this.buildResultPageUrl(shouldSendEmail);
    window.location.href = resultPageUrl;
  }

  getWebsizeInfoPayload(quizSession, shouldSendEmail = false) {
    const totalUsage = Array.isArray(quizSession.usageByDay)
      ? quizSession.usageByDay.reduce((sum, day) => sum + (Number(day?.units) || 0), 0)
      : 0;
    const resultPageUrl = this.buildResultPageUrl(shouldSendEmail);
    const absoluteResultPageUrl = `${window.location.origin}${resultPageUrl}`;

    return {
      contact: {
        email: quizSession.contact?.email || '',
        phone: quizSession.contact?.phone || ''
      },
      quiz: {
        name: quizSession.name || '',
        cycleDays: quizSession.cycleDays || 0,
        productType: quizSession.currentProductType || '',
        size: quizSession.size || '',
        style: quizSession.style || '',
        color: quizSession.color || '',
        usageByDay: totalUsage,
        link: absoluteResultPageUrl
      }
    };
  }

  async sendWebsizeInfo(quizSession, shouldSendEmail = false) {
    const endpoint = 'https://webserver.healthfab.in/api/websizeinfo';
    const payload = this.getWebsizeInfoPayload(quizSession, shouldSendEmail);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`websizeinfo request failed with status ${response.status}`);
      }
    } catch (error) {
      // Do not block checkout flow if this integration fails.
      console.error('Error sending websizeinfo payload:', error);
    }
  }

  async sendEmail(quizSession) {
    // This will call Google Apps Script middleware to send email via Zoho
    // The actual endpoint URL should be configured by the Healthfab team
    // Replace 'YOUR_GOOGLE_APPS_SCRIPT_URL' with the actual Google Apps Script web app URL
    const scriptUrl = 'YOUR_GOOGLE_APPS_SCRIPT_URL'; // To be configured - Get this from Healthfab team
    
    try {
      const response = await fetch(scriptUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: quizSession.contact.email,
          name: quizSession.name,
          type: 'customer'
        })
      });
      
      if (!response.ok) {
        throw new Error('Email sending failed');
      }
    } catch (error) {
      console.error('Email error:', error);
      // Don't block the flow if email fails
    }
  }
}

// Initialize quiz when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PeriodKitQuiz();
  });
} else {
  new PeriodKitQuiz();
}
